import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Lock, LockOpen } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../lib/api';

const COLORS = {
  A: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  B: 'bg-amber-100 text-amber-800 border-amber-300',
  C: 'bg-rose-100 text-rose-800 border-rose-300',
};

/**
 * Compact ABC velocity chip rendered inline on each SKU row.
 *  - Read-only for non-admin users (renders a pill or nothing).
 *  - Admin click opens a popover to manually pin a class (locks future
 *    recomputes), or to clear an existing override.
 *
 * `onChanged` is fired with the fresh `{abc_class, abc_class_locked}` so
 * the parent list can update its local state without a full refetch.
 */
export default function AbcChip({ sku, isAdmin, onChanged, layout = 'desktop' }) {
  const cls = sku?.abc_class;
  const locked = !!sku?.abc_class_locked;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const tid = (suffix) => `sku-abc-${suffix}-${layout}-${sku.sku_code}`;

  if (!cls && !isAdmin) return null;

  const apply = async (newClass, lockIt) => {
    setBusy(true);
    try {
      const r = await api.put(`/picking/skus/${sku.id}/abc`, {
        abc_class: newClass,
        locked: !!lockIt,
      });
      onChanged?.(r.data);
      toast.success(newClass
        ? `${sku.sku_code} pinned to ${newClass}-class`
        : `${sku.sku_code} override cleared`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Override failed');
    } finally { setBusy(false); }
  };

  const Chip = (
    <span
      data-testid={tid('chip')}
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border rounded ${
        cls ? COLORS[cls] : 'bg-zinc-100 text-zinc-500 border-zinc-300'
      } ${isAdmin ? 'cursor-pointer hover:opacity-80' : ''}`}
      title={
        cls
          ? `${cls}-class velocity${locked ? ' · pinned (recompute will not touch this SKU)' : ' · auto from pick history'}`
          : 'Unclassified — run "Recompute ABC" or pin manually'
      }
    >
      {cls || '—'}
      {locked && <Lock size={8} weight="fill" data-testid={tid('locked')} />}
    </span>
  );

  if (!isAdmin) return Chip;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={tid('trigger')} className="inline-flex">
          {Chip}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-3" align="start" data-testid={tid('popover')}>
        <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">Pin ABC class</div>
        <div className="grid grid-cols-3 gap-1 mb-2">
          {['A', 'B', 'C'].map((c) => (
            <Button
              key={c}
              size="sm"
              variant={cls === c ? 'default' : 'outline'}
              data-testid={`sku-abc-set-${c}-${layout}-${sku.sku_code}`}
              disabled={busy}
              onClick={() => apply(c, true)}
              className="h-8"
            >
              {c}
            </Button>
          ))}
        </div>
        {(cls || locked) && (
          <Button
            variant="ghost"
            size="sm"
            data-testid={tid('clear')}
            disabled={busy}
            onClick={() => apply(null, false)}
            className="w-full text-xs"
          >
            <LockOpen size={12} className="mr-1" />Clear override (auto-recompute)
          </Button>
        )}
        <p className="text-[10px] text-muted-foreground mt-2">
          Pinned classes are preserved by future ABC recomputes.
        </p>
      </PopoverContent>
    </Popover>
  );
}
